import React, { DependencyList, FC, PropsWithChildren, useState } from "react";
import {
  faAngleUp,
  faBars,
  faCalendar,
  faCheck,
  faCog,
  faCogs,
  faExclamationTriangle,
  faFileImport,
  faFlask,
  faInfoCircle,
  faLayerGroup,
  faList,
  faPlus,
  faSearchDollar,
  faSignal,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatCurrency, formatDecimal, useEffectLoader, useLoader } from "ackbox-weblib-react";

library.add(
  faCogs,
  faCog,
  faSearchDollar,
  faExclamationTriangle,
  faBars,
  faTasks,
  faCalendar,
  faFlask,
  faPlus,
  faList,
  faFileImport,
  faLayerGroup,
  faSignal,
  faAngleUp,
  faCheck,
  faInfoCircle,
);

export const RESOURCES_UI_PATH = "/#/resources";
export const RESOURCES_API_PATH = "/api/resources";
export const PROGRESS_API_PATH = "/api/progress";
export const SUMMARIES_API_PATH = "/api/summaries";
export const TRANSACTIONS_IMPORT_API_PATH = "/api/transactions/import";

/** The supported currency types. */
export enum CurrencyType {
  USD = "USD",
  CAD = "CAD",
}

export const COLORS = [
  "#1abc9c",
  "#16a085",
  "#f1c40f",
  "#f39c12",
  "#d35400",
  "#c0392b",
  "#3498db",
  "#2980b9",
  "#34495e",
  "#2c3e50",
  "#bdc3c7",
  "#95a5a6",
];

const decoratedClass = (amount: number) => {
  let modifier = "";
  if (amount > 0) {
    modifier = "has-text-primary";
  } else if (amount < 0) {
    modifier = "has-text-danger";
  }
  return modifier;
};

export const formatDecoratedCurrency = (amount: number) => {
  const modifier = decoratedClass(amount);
  return <span className={modifier}>{formatCurrency(amount)}</span>;
};

export const formatDecoratedPercentage = (amount: number) => {
  const modifier = decoratedClass(amount);
  return <span className={modifier}>{formatDecimal(amount)}%</span>;
};

export const Empty = () => {
  return <h2 className="subtitle is-size-6">No results yet.</h2>;
};

export const Accordion: FC<PropsWithChildren<{ title: string }>> = (props) => {
  const [expanded, setExpanded] = useState(false);
  const toggleState = () => setExpanded(!expanded);
  const preventRedirect = (e: React.MouseEvent<HTMLAnchorElement>) =>
    e.preventDefault();
  return (
    <div className="">
      <div className="card" aria-hidden={expanded ? "false" : "true"}>
        <header
          className="card-header"
          style={{ cursor: "pointer" }}
          onClick={toggleState}
        >
          <p className="card-header-title">{props.title}</p>
          <a href="#/" onClick={preventRedirect} className="card-header-icon">
            <span
              className="icon"
              style={{
                transform: expanded ? "unset" : "rotate(180deg)",
                transition: "transform 250ms ease-out",
              }}
            >
              <FontAwesomeIcon icon="angle-up"/>
            </span>
          </a>
        </header>
        <div
          className="card-content"
          style={{
            maxHeight: expanded ? "100%" : 0,
            padding: expanded ? "unset" : 0,
            overflow: "hidden",
            transition: "max-height 100ms ease-in-out",
          }}
        >
          <div className="content">{props.children} </div>
        </div>
      </div>
    </div>
  );
};

export const useEffectOnce = (
  load: () => void,
  deps?: DependencyList,
): { errors: string[]; isLoading: boolean } => {
  const ackLoader = useLoader();
  const dependencies = deps == null ? [] : deps;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffectLoader(ackLoader, load, dependencies);
  const errors = ackLoader.errors;
  const isLoading = ackLoader.isLoading;
  return { errors, isLoading };
};
